<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('menus.resourceLimits.resourceLimits') }}</h2>
      </v-col>
    </v-row>

    <v-card elevation=0>
      <v-container fluid>
        <!-- Header Row -->
        <v-row class="border-bottom py-2 text-uppercase font-weight-bold">
          <v-col cols="3" md="2">{{ $t('routes.usage.resource') }}</v-col>
          <v-col cols="3" md="2" class="text-center">{{ $t('menus.resourceLimits.usage') }}</v-col>
          <v-col cols="0" md="5" v-show="$vuetify.display.mdAndUp">&nbsp;</v-col>
          <v-col cols="3" md="2" class="text-center">{{ $t('menus.resourceLimits.limit') }}</v-col>
        </v-row>

        <!-- Data Rows -->
        <v-row class="py-2" :key="resource.id" v-for="resource in resources">
          <v-col cols="3" md="2">{{ $t(`routes.usage.${resource.name}`) }}</v-col>
          <v-col cols="3" md="2" class="text-center">{{ formatValue(resource.name, resource.spent) }}</v-col>
          <v-col cols="0" md="5" v-show="$vuetify.display.mdAndUp">
            <v-progress-linear
                :model-value="calculateProgress(resource.limit, resource.spent)"
                color="primary"
                height="16px"
                rounded
            >
              <span>{{ calculateProgress(resource.limit, resource.spent) + '%' }}</span>
            </v-progress-linear>
          </v-col>
          <v-col cols="3" md="2" class="text-center">{{ formatValue(resource.name, resource.limit) }}</v-col>
          <v-col cols="3" md="1">
            <v-tooltip
              v-if="$canPurchaseResources()"
              :text="$t(`routes.usage.purchaseAdditionalResource`, { resource: $t(`routes.usage.${resource.name}`).toLowerCase() })"
              location="bottom"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="flex-grow-0"
                  density="compact"
                  rounded="circle"
                  size="32"
                  variant="flat"
                  color="primary"
                  icon
                  @click="openPurchaseDialog(resource)"
                >
                  <v-icon size="20">mdi-cart-plus</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </v-col>
          <v-col cols="12" md="0" v-show="$vuetify.display.smAndDown">
            <v-progress-linear
                :model-value="calculateProgress(resource.limit, resource.spent)"
                color="primary"
                height="16px"
                rounded
            >
              <span>{{ calculateProgress(resource.limit, resource.spent) + '%' }}</span>
            </v-progress-linear>
          </v-col>
        </v-row>

      </v-container>
    </v-card>

    <v-row>
      <v-col>
        <h3 class="mb-3 mt-3 ml-3">{{ $t('routes.usage.resourceUsage') }} </h3>
      </v-col>
    </v-row>

    <v-card elevation=0>
      <v-container fluid>
        <v-row class="border-bottom py-2">
          <v-col cols="md-2 sm-6">{{ $t(`routes.usage.foldersCreated`) }}</v-col>
          <v-col cols="md-10 sm-6">{{ createdFolders }}</v-col>
        </v-row>
        <v-row class="border-bottom py-2">
          <v-col cols="md-2 sm-6">{{ $t(`routes.usage.filesUploaded`) }}</v-col>
          <v-col cols="md-10 sm-6">{{ uploadedFiles }}</v-col>
        </v-row>
        <v-row class="border-bottom py-2">
          <v-col cols="md-2 sm-6">{{ $t(`routes.usage.createdUsers`) }}</v-col>
          <v-col cols="md-10 sm-6">{{ createdUsers }}</v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-row>
      <v-col>
        <h3 class="mb-3 mt-3 ml-3">{{ $t('routes.usage.recentlyUsedServices') }} </h3>
      </v-col>
    </v-row>

    <v-card elevation=0 class="vb-latest-services-usage">
      <v-container fluid>
        <VDataTableServer
            :headers="headers"
            :items="serviceLogsData"
            :items-per-page="itemsPerPage"
            v-model:page="currentPage"
            :items-length="totalItems"
            :loading="loading"
            class="elevation-0"
            @update:items-per-page="itemsPerPageChanged"
        >
          <!-- Updated slot names for Vuetify 3 -->
          <template v-slot:item.servicealias="{ item }">
            <span>{{ item.serviceName || item.servicealias }}</span>
          </template>

          <template v-slot:item.duration="{ item }">
            <span v-if="item.formattedDuration">{{ item.formattedDuration }}</span>
          </template>

          <template v-slot:item.node="{ item }">
            <router-link v-if="item.node" :to="'/node/' + item.node.id">{{ item.node.name }}</router-link>
            <span v-else>---</span>
          </template>

          <template v-slot:item.pod="{ item }">
            <router-link v-if="item.pod" :to="'/pod/' + item.pod.id">{{ item.pod.name }}</router-link>
            <span v-else>---</span>
          </template>
        </VDataTableServer>
      </v-container>
    </v-card>
  </div>

  <PurchaseAdditionalResourceDialog
    ref="purchaseDialog"
    :resource-type="selectedResource?.name"
    :current-usage="selectedResource?.spent"
    :current-limit="selectedResource?.limit"
    @resource-purchased="onResourcePurchased"
  />
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore'
import { useAccountStore } from '@/stores/AccountStore.js'
import PurchaseAdditionalResourceDialog
  from '@/routes/console/usage/PurchaseAdditionalResourceDialog.vue';

export default {
  name: "SubscriptionUsage",
  components: { PurchaseAdditionalResourceDialog },
  data() {
    return {
      accessPoints: [],
      createdUsers: 0,
      createdFolders: 0,
      uploadedFiles: 0,
      resources: [],
      isUnmounted: false,
      selectedResource: null,

      headers: [
        { title: this.$t('routes.usage.service'), key: 'serviceName' }, // Changed: text -> title, value -> key
        { title: this.$t('routes.usage.duration'), key: 'duration' },
        { title: this.$t('routes.usage.folder'), key: 'node' },
        { title: this.$t('routes.usage.file'), key: 'pod' },
      ],
      currentPage: 1,  // Current page
      itemsPerPage: 15,  // Number of items per page (limit)
      totalItems: 0,  // Total number of items (this could be set from the server response)
      loading: false,
      serviceLogs: [],
      serviceLogsData: [],
    };
  },
  afterCreate() {
    if (!this.$canControlTenantConfig() && !this.$canBelongstoTeam()) {
      this.$router.push('/console/index');
    }
  },
  beforeUnmount() {
    this.isUnmounted = true;
  },
  async mounted() {
    if (!this.$canControlTenantConfig()) {
      this.$router.push('/console/index');
      return; // It's good practice to return after a redirect
    }

    this.loading = true;

    const payload = {
      tenantId: this.activeTenantId,
    };

    const accountStore = useAccountStore();

    try {
      const data = await accountStore.getUsage(payload);

      if (this.isUnmounted) {
        // Possible race condition when switching to another console section,
        // before the request has finished loading.
        return;
      }

      this.accessPoints = data.accessPoints;
      this.resources = data.resources;
      this.createdUsers = data.usersByCreator;
      this.uploadedFiles = data.podsCounts;
      this.createdFolders = data.nodesCount;
      this.serviceLogs = data.serviceLogs;

      this.totalItems = data.serviceLogs.length;

      this.processServiceLogs();

    } catch (error) {
      if (error && error.response && error.response.data && error.response.data.error) {
        const localizedMessage = this.$localizeMessage(error.response.data.error, this.$t('backend.system-error'));
        this.$showMessage(localizedMessage, { type: 'error' });
      } else {
        console.log(error);
        this.$showMessage(this.$t('backend.system-error'), { type: 'error' });
      }
    } finally {
      this.loading = false;
    }
  },
  watch: {
    currentPage() {
      this.processServiceLogs();
    }
  },
  computed: {
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    activeTenantId: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenantId;
    },
  },
  methods: {
    formatValue(resourceName, value) {
      if (['bandwidth', 'storage'].includes(resourceName)) {

        let valueInGB = BigInt(value) / BigInt(1e9);
        if (valueInGB < 1) {

          return (BigInt(value) / BigInt(1e6)).toString() + " MB";  // Convert bytes to MB
        }

        return valueInGB.toString() + " GB"; // Convert bytes to GB
      }

      return value.toString();
    },

    calculateProgress(limit, spent) {
      // Ensure limit is not zero to prevent division by zero


      const bigIntLimit = BigInt(limit);

      const bigIntSpent = BigInt(spent);

      if (bigIntLimit === 0n) return 0;

      // Calculate percentage
      let progress = (bigIntSpent * 100n) / bigIntLimit;

      // Convert BigInt to Number and return
      return Number(progress.toString());
    },

    formatDuration(durationInMilliseconds) {
      const translationMinutes = this.$t('common.minutes');
      const translationSeconds = this.$t('common.seconds');

      // Convert milliseconds to seconds
      const durationInSeconds = durationInMilliseconds / 1000;

      if (durationInSeconds < 60) {
        return `${Math.round(durationInSeconds)} ${translationSeconds}`;
      } else {
        const minutes = Math.floor(durationInSeconds / 60);
        const remainingSeconds = Math.round(durationInSeconds % 60);
        return `${minutes} ${translationMinutes} ${remainingSeconds > 0 ? `${remainingSeconds} ${translationSeconds}` : ''}`.trim();
      }
    },

    processServiceLogs() {
       const items = this.serviceLogs.slice(
          (this.currentPage - 1) * this.itemsPerPage,
          this.currentPage * this.itemsPerPage
      );

       for (const item of items) {
         item.serviceName = this.$t(`service.${item.servicealias}`);
         item.formattedDuration = this.formatDuration(item.duration);
       }

      this.serviceLogsData = items;
    },

    itemsPerPageChanged(value) {
      this.currentPage = 1;
      this.itemsPerPage = value;
      this.processServiceLogs();
    },

    openPurchaseDialog(resource) {
      this.selectedResource = resource;
      this.$refs.purchaseDialog.openDialog();
    },

    async onResourcePurchased() {
      this.loading = true;

      try {
        const accountStore = useAccountStore();
        const data = await accountStore.getUsage({
          tenantId: this.activeTenantId,
        });

        if (this.isUnmounted) {
          return;
        }

        this.resources = data.resources;
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>

<style>
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12); /* This provides a light separator line between rows */
}
.text-uppercase {
  text-transform: uppercase;
}
.font-weight-bold {
  font-weight: bold;
}

.vb-latest-services-usage .v-data-table-header > tr > th {
  font-size: large !important;
  font-weight: normal !important;
}
</style>
